import * as THREE from 'three';
import type { NpcBrain, NpcBrainContext, PatrolBeat } from './brain';
import { applyThrust, faceTarget, pickRandomPoint, steerToward } from './brain';

type GuardState = 'patrol' | 'combat' | 'returning' | 'finished';

/**
 * Combat is modelled on real energy-fighter doctrine, not WWI dogfights.
 *
 *   approach → merge → extend → reverse → approach
 *
 * Each phase is single-purpose so the physics integrator never gets a
 * conflicting blend of "turn back to target" + "fly past target":
 *
 * - approach: lag pursuit with a lateral aim offset so the closure path
 *             naturally clears the target by the offset distance.
 * - merge:    the committed pass. Steering is locked, thrust biased
 *             outward (away from the player) so accumulated forward
 *             velocity is bled sideways instead of into the player.
 * - extend:   the anti-circle phase. Ship rotates to face directly away
 *             from the player and burns straight, building separation.
 *             This is what kills the orbiting/strafing read.
 * - reverse:  high-rate bank back toward the target after enough range
 *             has been built. Throttle stays open so we re-enter approach
 *             with energy, not stalled.
 */
type CombatPhase = 'approach' | 'merge' | 'extend' | 'reverse' | 'break';

const PATROL_SPEED = 44;
// Slight aggression bump — guards close on the player a hair faster
// during the approach phase. Other phase speeds left untouched so the
// committed pass and break-off still feel measured.
const APPROACH_SPEED = 88;
// Boost during the pass so the merge feels committed and fast — also helps
// momentum carry the ship past the target instead of stalling next to it.
const MERGE_SPEED = 96;
const EXTEND_SPEED = 92;
const REVERSE_SPEED = 70;
const ACCEL = 3.6;

const PATROL_TURN = 2.8;
// Slight bump (was 3.0) — tracks the player a touch tighter during
// the approach so the firing window opens sooner.
const APPROACH_TURN = 3.6;
// Critical: very low merge turn rate means faceTarget barely moves the
// quaternion. The ship commits to the pass instead of correcting back into
// the player as it gets close.
const MERGE_TURN = 0.6;
const EXTEND_TURN = 2.4;
const REVERSE_TURN = 5.6;

const DETECTION_RANGE = 350;
const LOSE_RANGE = 750;

// Approach / pass / extend / reverse tuning. The round-trip is
// short and the merge actually grazes the player so a fight reads
// like a dogfight instead of a chase across the sector.
const ATTACK_RANGE = 360;
const MERGE_TRIGGER_DIST = 200;
const FIRE_MIN_DIST = 50;
// Bumped back from 0.13 — 0.18 keeps a real DPS but the guards no
// longer sustain bullets ~70% of the time. Combined with phases
// where they're flying away (extend) silent, total fire-time
// settles around 50–55%, leaving the player breathing room.
const FIRE_INTERVAL = 0.18;
// Anti-stall thresholds — the guard is allowed to pass close at
// speed, what we forbid is *lingering* close. The accumulator below
// only forces an outward kick after the guard has been close AND
// slow for STALL_TIME continuous seconds, so a normal fly-by never
// trips this.
const STALL_RADIUS = 180;
const STALL_SPEED = 18;
const STALL_TIME = 0.6;

// Break maneuver — fires when the player chases and lands hits while
// the guard is in extend (otherwise predictable straight-line flight).
// The guard banks hard onto a perpendicular vector and re-engages
// from a new angle. Tuned to feel sharp but human, not robotic.
const BREAK_DURATION_MIN = 0.45;
const BREAK_DURATION_MAX = 0.7;
const BREAK_TURN = 6.5;
const BREAK_SPEED = 104;
const BREAK_ACCEL = 5.0;
// Cooldown so a gunfight that lands shots every frame doesn't chain
// breaks back-to-back — gives the guard a beat between maneuvers to
// fire and reposition.
const BREAK_COOLDOWN = 1.2;
const BREAK_VERTICAL_BIAS = 0.25;
// Slightly looser than before (was 0.9 / ~25°) so the higher cadence
// translates to more shots actually leaving the muzzle during a pass.
// 0.86 ≈ 30° half-angle.
const FIRE_CONE = 0.86;
const PROJECTILE_SPEED = 220;
const GUARD_DAMAGE = 1;

// Extend phase: a brief separation breath before committing to the
// next pass. Kept very short so a chasing player can never feel
// like the guard is "fleeing" — within ~0.5s the guard is already
// turning back to fire.
const EXTEND_DURATION_MIN = 0.3;
const MIN_REVERSE_SEPARATION = 140;
// Cap so a perfectly-aligned pass that overshoots doesn't fly forever.
const EXTEND_DURATION_MAX = 0.55;

// Reverse phase: declare the turn complete when we're roughly nose-on AND
// distance is decreasing. Min time prevents instant-flip bugs from very
// shallow reversal angles.
const REVERSE_MIN_TIME = 0.35;
const REVERSE_ALIGNED_DOT = 0.86;

// Aim offset is a 2D vector in the plane perpendicular to LOS, projected
// to world space each tick. Vertical bias < 1 keeps passes more horizontal
// than diving — fighters prefer the horizontal plane in level engagements.
// Tightened so the lead/aim point sits much closer to the player —
// passes actually graze instead of sliding 60+u to the side.
const AIM_OFFSET_RADIUS = 25;
const AIM_OFFSET_VERTICAL_BIAS = 0.42;
// During merge, blend this fraction of "away from player" into the thrust
// direction. Pure forward thrust would still deliver the ship into the
// player due to velocity lag; the outward bias actively curves us out.
// Reduced so the merge actually grazes the player — the anti-stall
// safeguard handles the rare case of a guard hovering close, so we
// don't need to bake escape thrust into every pass.
const MERGE_OUTWARD_BIAS = 0.28;

const STATION_DESPAWN_RANGE = 120;
const WAYPOINT_REACH = 60;
const WORLD_UP = new THREE.Vector3(0, 1, 0);

const _muzzleWorld = new THREE.Vector3();
const _shotDir = new THREE.Vector3();
const _toTarget = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _aimPoint = new THREE.Vector3();
const _away = new THREE.Vector3();
const _thrustDir = new THREE.Vector3();
const _perpA = new THREE.Vector3();
const _perpB = new THREE.Vector3();
const _leadPoint = new THREE.Vector3();
const _toLead = new THREE.Vector3();
const _hubPos = new THREE.Vector3();
const MUZZLE_OFFSET = new THREE.Vector3(0, 0, -2.2);

export interface GuardBrainConfig {
  readonly patrolWaypoints: number;
  /** When true the brain spawns already in `combat` state with
   *  hostility on. Used by the tutorial pirate mission so the ship
   *  immediately attacks instead of going through the patrol loop. */
  readonly hostileFromSpawn?: boolean;
  /** When true the brain never returns to the patrol roster — once
   *  combat ends (player out of range or destroyed) the brain marks
   *  itself finished so the director despawns it. Pairs naturally
   *  with `hostileFromSpawn: true` for one-shot encounter ships. */
  readonly noPatrolReturn?: boolean;
}

export class GuardBrain implements NpcBrain {
  finished = false;
  private state: GuardState;
  private hostile: boolean;
  private readonly waypoint = new THREE.Vector3();
  private waypointsVisited = 0;
  private fireCooldown = 0;

  private combatPhase: CombatPhase = 'approach';
  private phaseTimer = 0;
  /** Accumulator that grows while the guard is close AND moving slowly,
   *  resets the moment either condition fails. Triggers the
   *  anti-stall outward kick when it crosses STALL_TIME. */
  private stallTimer = 0;
  private aimOffsetRight = 0;
  private aimOffsetUp = 0;
  /** Remaining seconds of an active break maneuver. > 0 means
   *  `runBreak` is driving the ship; phase transitions wait. */
  private breakDuration = 0;
  /** Wall-clock-equivalent cooldown — counts down every tick after a
   *  break so a barrage of player hits doesn't loop breaks
   *  indefinitely. The guard fires + repositions naturally between
   *  maneuvers. */
  private breakCooldown = 0;
  private readonly breakDirection = new THREE.Vector3();

  /** Patrol assignment from the director. Held for the lifetime of this
   *  brain and released on dispose so the next guard can claim the slot. */
  private readonly patrolBeat: PatrolBeat;
  /** For lane beats: alternates 0/1 so successive waypoints flip between
   *  the two endpoints — this is what produces the "shuttle between hubs"
   *  read instead of orbiting one of them. */
  private laneToggle: 0 | 1 = 0;

  constructor(
    private readonly ctx: NpcBrainContext,
    private readonly config: GuardBrainConfig
  ) {
    this.patrolBeat = ctx.patrolBeats.acquire();
    if (this.patrolBeat.kind === 'lane') {
      // Random initial endpoint so spawn waves don't all enter from the
      // same side of every lane.
      this.laneToggle = Math.random() < 0.5 ? 0 : 1;
    }
    this.pickPatrolWaypoint();
    this.randomizeAimOffset();
    // Pirate-mode brains skip patrol entirely — hostile + combat from
    // frame zero. Guards default to the patrol → combat flow.
    if (config.hostileFromSpawn) {
      this.hostile = true;
      this.state = 'combat';
      this.combatPhase = 'approach';
      this.phaseTimer = 0;
    } else {
      this.hostile = false;
      this.state = 'patrol';
    }
  }

  isHostile(): boolean {
    return this.hostile;
  }

  onAttackedByPlayer(): void {
    this.hostile = true;
    if (this.state === 'patrol' || this.state === 'returning') {
      this.enterCombat();
      return;
    }
    // Already engaging — if the player landed a hit while we're in
    // a predictable straight-line phase (extend, mid-merge), break
    // hard onto a perpendicular vector so they can't sit on our six.
    // The cooldown keeps the break from chaining on every shot.
    if (
      this.state === 'combat' &&
      this.breakCooldown <= 0 &&
      this.breakDuration <= 0 &&
      (this.combatPhase === 'extend' || this.combatPhase === 'merge')
    ) {
      this.startBreakManeuver();
    }
  }

  clearHostility(): void {
    this.hostile = false;
    if (this.state === 'combat') {
      this.state = 'patrol';
      this.pickPatrolWaypoint();
    }
  }

  update(dt: number): void {
    this.fireCooldown = Math.max(0, this.fireCooldown - dt);
    this.breakCooldown = Math.max(0, this.breakCooldown - dt);

    if (this.hostile && this.state !== 'combat' && this.state !== 'finished') {
      this.enterCombat();
    }

    switch (this.state) {
      case 'patrol':
        this.updatePatrol(dt);
        break;
      case 'combat':
        this.updateCombat(dt);
        break;
      case 'returning':
        this.updateReturning(dt);
        break;
      case 'finished':
        break;
    }
  }

  dispose(): void {
    this.ctx.patrolBeats.release(this.patrolBeat);
  }

  private updatePatrol(dt: number): void {
    const distance = steerToward(
      this.ctx.ship,
      this.waypoint,
      dt,
      PATROL_SPEED,
      ACCEL,
      PATROL_TURN
    );
    if (distance < WAYPOINT_REACH) {
      this.waypointsVisited++;
      if (this.waypointsVisited >= this.config.patrolWaypoints) {
        this.state = 'returning';
      } else {
        this.pickPatrolWaypoint();
      }
    }
  }

  private enterCombat(): void {
    this.state = 'combat';
    this.combatPhase = 'approach';
    this.phaseTimer = 0;
    this.randomizeAimOffset();
  }

  /** Pick a perpendicular evasion vector and switch to the break
   *  phase. Direction is chosen once at maneuver start and held for
   *  the whole break, so the trajectory reads as a deliberate hard
   *  turn rather than a confused wobble. */
  private startBreakManeuver(): void {
    const player = this.ctx.getPlayerShip();
    if (!player) return;
    _toTarget
      .copy(player.object3D.position)
      .sub(this.ctx.ship.object3D.position);
    const dist = _toTarget.length() || 1;
    _toTarget.divideScalar(dist);
    // Horizontal perpendicular to the LOS — flip side at random so a
    // chasing player can't pre-aim.
    this.breakDirection.crossVectors(_toTarget, WORLD_UP);
    if (this.breakDirection.lengthSq() < 1e-4) {
      this.breakDirection.set(1, 0, 0);
    }
    this.breakDirection.normalize();
    if (Math.random() < 0.5) this.breakDirection.multiplyScalar(-1);
    // Slight vertical component so the break also breaks the firing
    // plane, not just the heading.
    this.breakDirection.y += (Math.random() - 0.5) * 2 * BREAK_VERTICAL_BIAS;
    this.breakDirection.normalize();

    this.combatPhase = 'break';
    this.phaseTimer = 0;
    this.breakDuration =
      BREAK_DURATION_MIN +
      Math.random() * (BREAK_DURATION_MAX - BREAK_DURATION_MIN);
  }

  /** Hard-turn maneuver triggered when the player lands hits in a
   *  predictable phase. Banks toward `breakDirection`, accelerates,
   *  and transitions to reverse so the guard re-engages from the new
   *  angle instead of rerunning the broken extend. */
  private runBreak(
    dt: number,
    distance: number,
    playerPos: THREE.Vector3,
    playerVel: THREE.Vector3
  ): void {
    this.breakDuration -= dt;

    _aimPoint
      .copy(this.ctx.ship.object3D.position)
      .addScaledVector(this.breakDirection, 200);
    faceTarget(this.ctx.ship, _aimPoint, dt, BREAK_TURN);

    _forward.set(0, 0, -1).applyQuaternion(this.ctx.ship.object3D.quaternion);
    applyThrust(this.ctx.ship, _forward, dt, BREAK_SPEED, BREAK_ACCEL);

    // Opportunistic shot if the bank happens to bring the player into
    // the cone — rare, but feels great when it lands.
    _toTarget.copy(playerPos).sub(this.ctx.ship.object3D.position).normalize();
    if (
      _forward.dot(_toTarget) >= FIRE_CONE &&
      distance > FIRE_MIN_DIST &&
      distance < ATTACK_RANGE &&
      this.fireCooldown <= 0
    ) {
      this.fireWithLead(playerPos, playerVel);
      this.fireCooldown = FIRE_INTERVAL;
    }

    if (this.breakDuration <= 0) {
      // Lock the cooldown so subsequent hits don't immediately
      // chain another break — the guard gets a beat to fire from
      // the new angle through reverse + approach.
      this.breakCooldown = BREAK_COOLDOWN;
      this.combatPhase = 'reverse';
      this.phaseTimer = 0;
    }
  }

  private updateCombat(dt: number): void {
    const player = this.ctx.getPlayerShip();
    if (!player) {
      this.hostile = false;
      this.state = 'patrol';
      this.pickPatrolWaypoint();
      return;
    }

    const shipPos = this.ctx.ship.object3D.position;
    const playerPos = player.object3D.position;
    const playerVel = player.velocity;
    _toTarget.copy(playerPos).sub(shipPos);
    const distance = _toTarget.length();

    if (distance > LOSE_RANGE) {
      // One-shot encounter ships (pirates / scripted ambushes) flag
      // themselves finished so the director despawns instead of
      // putting them on the patrol roster.
      if (this.config.noPatrolReturn) {
        this.finished = true;
        return;
      }
      this.hostile = false;
      this.state = 'patrol';
      this.pickPatrolWaypoint();
      return;
    }

    this.phaseTimer += dt;

    // Anti-stall: only triggers after the guard has been *both* close
    // AND slow for STALL_TIME continuous seconds. A normal fly-by
    // pass keeps speed high enough that the timer never accumulates,
    // so the player is allowed to be brushed past at close range
    // — what we forbid is lingering. When the timer trips, force an
    // outward velocity kick + commit to extend so the guard slides
    // off rather than orbits.
    const speed = this.ctx.ship.velocity.length();
    if (distance < STALL_RADIUS && speed < STALL_SPEED) {
      this.stallTimer += dt;
    } else {
      this.stallTimer = 0;
    }
    if (this.stallTimer >= STALL_TIME) {
      _away.copy(this.ctx.ship.object3D.position).sub(playerPos);
      if (_away.lengthSq() < 1e-4) _away.set(0, 0, 1);
      _away.normalize();
      this.ctx.ship.velocity.copy(_away).multiplyScalar(MERGE_SPEED);
      this.combatPhase = 'extend';
      this.phaseTimer = 0;
      this.stallTimer = 0;
      return;
    }

    switch (this.combatPhase) {
      case 'approach':
        this.runApproach(dt, distance, playerPos, playerVel);
        break;
      case 'merge':
        this.runMerge(dt, playerPos, playerVel);
        break;
      case 'extend':
        this.runExtend(dt, distance, playerPos);
        break;
      case 'reverse':
        this.runReverse(dt, distance, playerPos, playerVel);
        break;
      case 'break':
        this.runBreak(dt, distance, playerPos, playerVel);
        break;
    }
  }

  /** Lag pursuit with lateral offset. Closes the range on a path that's
   *  already biased to fly past the target, so the merge inherits that
   *  geometry instead of having to invent it. */
  private runApproach(
    dt: number,
    distance: number,
    playerPos: THREE.Vector3,
    playerVel: THREE.Vector3
  ): void {
    this.computeLeadPoint(distance, playerPos, playerVel, _leadPoint);
    this.computeLateralAim(_leadPoint, _aimPoint);

    faceTarget(this.ctx.ship, _aimPoint, dt, APPROACH_TURN);

    _thrustDir.copy(_aimPoint).sub(this.ctx.ship.object3D.position).normalize();
    applyThrust(this.ctx.ship, _thrustDir, dt, APPROACH_SPEED, ACCEL);

    _forward.set(0, 0, -1).applyQuaternion(this.ctx.ship.object3D.quaternion);
    _toLead.copy(_leadPoint).sub(this.ctx.ship.object3D.position).normalize();
    const aligned = _forward.dot(_toLead) >= FIRE_CONE;
    if (
      aligned &&
      distance > FIRE_MIN_DIST &&
      distance < ATTACK_RANGE &&
      this.fireCooldown <= 0
    ) {
      this.fireWithLead(playerPos, playerVel);
      this.fireCooldown = FIRE_INTERVAL;
    }

    if (distance < MERGE_TRIGGER_DIST) {
      this.combatPhase = 'merge';
      this.phaseTimer = 0;
    }
  }

  /** The merge: committed pass with deliberately lazy steering. Thrust is
   *  blended outward from the player so velocity lag (which would otherwise
   *  carry the ship into the target) gets bled into a sideways drift.
   *
   *  Air-combat spirit: the guard keeps firing through the entire merge as
   *  long as the player is in front of the cone. The pass-through silence
   *  was reading as "circling without shooting" — now any frame the nose
   *  is on the player gets a round. */
  private runMerge(
    dt: number,
    playerPos: THREE.Vector3,
    playerVel: THREE.Vector3
  ): void {
    _forward.set(0, 0, -1).applyQuaternion(this.ctx.ship.object3D.quaternion);

    _away.copy(this.ctx.ship.object3D.position).sub(playerPos);
    const awayLen = _away.length() || 1;
    _away.multiplyScalar(1 / awayLen);

    _thrustDir
      .copy(_forward)
      .addScaledVector(_away, MERGE_OUTWARD_BIAS)
      .normalize();
    applyThrust(this.ctx.ship, _thrustDir, dt, MERGE_SPEED, ACCEL);

    // Soft face toward the projected forward heading so the ship doesn't
    // freeze in roll, but turn rate is so low this barely budges the nose
    // — preserves the pass-through commitment.
    _aimPoint.copy(this.ctx.ship.object3D.position).addScaledVector(_forward, 200);
    faceTarget(this.ctx.ship, _aimPoint, dt, MERGE_TURN);

    // Fire if the player is still in the forward cone — distance gate
    // matches approach so we don't lose shots into the close window.
    _toTarget.copy(playerPos).sub(this.ctx.ship.object3D.position);
    const distance = _toTarget.length() || 1;
    _toTarget.divideScalar(distance);
    if (
      _forward.dot(_toTarget) >= FIRE_CONE &&
      distance > FIRE_MIN_DIST &&
      distance < ATTACK_RANGE &&
      this.fireCooldown <= 0
    ) {
      this.fireWithLead(playerPos, playerVel);
      this.fireCooldown = FIRE_INTERVAL;
    }

    // Transition to extend the moment the target is behind us. dot becomes
    // negative once we've actually crossed the merge plane.
    const passedTarget = _forward.dot(_toTarget) < -0.05;
    // Hard cap so we never get stuck if the player is keeping pace.
    if (passedTarget || this.phaseTimer > 1.4) {
      this.combatPhase = 'extend';
      this.phaseTimer = 0;
    }
  }

  /** Extend / separation phase. The ship rotates to face directly AWAY from
   *  the player and burns straight — this is the explicit anti-circling
   *  step that breaks the lock-on geometry. */
  private runExtend(
    dt: number,
    distance: number,
    playerPos: THREE.Vector3
  ): void {
    _away.copy(this.ctx.ship.object3D.position).sub(playerPos);
    const awayLen = _away.length() || 1;
    _away.multiplyScalar(1 / awayLen);

    // Aim point is "1000u further in the away direction" so faceTarget has
    // a stable far reference; using the unit vector directly would scale to
    // the ship's own position when added.
    _aimPoint.copy(this.ctx.ship.object3D.position).addScaledVector(_away, 1000);
    faceTarget(this.ctx.ship, _aimPoint, dt, EXTEND_TURN);

    _forward.set(0, 0, -1).applyQuaternion(this.ctx.ship.object3D.quaternion);
    applyThrust(this.ctx.ship, _forward, dt, EXTEND_SPEED, ACCEL);

    const longEnough = this.phaseTimer >= EXTEND_DURATION_MIN;
    const farEnough = distance >= MIN_REVERSE_SEPARATION;
    const timedOut = this.phaseTimer >= EXTEND_DURATION_MAX;
    if ((longEnough && farEnough) || timedOut) {
      this.combatPhase = 'reverse';
      this.phaseTimer = 0;
      this.randomizeAimOffset();
    }
  }

  /** Hard 180 back toward the target. Throttle stays high through the bank
   *  so we re-enter approach with energy, not stalled into a slow drift. */
  private runReverse(
    dt: number,
    distance: number,
    playerPos: THREE.Vector3,
    playerVel: THREE.Vector3
  ): void {
    faceTarget(this.ctx.ship, playerPos, dt, REVERSE_TURN);
    _forward.set(0, 0, -1).applyQuaternion(this.ctx.ship.object3D.quaternion);
    applyThrust(this.ctx.ship, _forward, dt, REVERSE_SPEED, ACCEL);

    _toTarget.copy(playerPos).sub(this.ctx.ship.object3D.position).normalize();
    const aligned = _forward.dot(_toTarget) >= REVERSE_ALIGNED_DOT;
    // Suppressive fire while reversing — the previous design left the
    // player a quiet window during the turn-back. With matching
    // cadence, any moment the guard's nose crosses the player it
    // shoots. Range gate stays the same so we don't snipe across the
    // sector.
    if (
      _forward.dot(_toTarget) >= FIRE_CONE &&
      distance > FIRE_MIN_DIST &&
      distance < ATTACK_RANGE &&
      this.fireCooldown <= 0
    ) {
      this.fireWithLead(playerPos, playerVel);
      this.fireCooldown = FIRE_INTERVAL;
    }
    if (
      this.phaseTimer >= REVERSE_MIN_TIME &&
      aligned &&
      distance > MERGE_TRIGGER_DIST * 0.85
    ) {
      this.combatPhase = 'approach';
      this.phaseTimer = 0;
      return;
    }
    // If the reverse pass ate the separation and the guard is now
    // flying directly into close range, commit to a merge fly-by
    // instead of pinning at point-blank. Without this, a reverse
    // started from too-close (e.g. extend timed out before reaching
    // MIN_REVERSE_SEPARATION) loops into the player forever.
    if (distance < MERGE_TRIGGER_DIST) {
      this.combatPhase = 'merge';
      this.phaseTimer = 0;
    }
  }

  /** Iterative lead solution: assume player keeps current velocity, find
   *  the impact point that satisfies time-of-flight = distance/projSpeed.
   *  Two iterations are enough at our distances and projectile speed. */
  private computeLeadPoint(
    distance: number,
    playerPos: THREE.Vector3,
    playerVel: THREE.Vector3,
    out: THREE.Vector3
  ): void {
    let tti = distance / PROJECTILE_SPEED;
    out.copy(playerPos).addScaledVector(playerVel, tti);
    for (let i = 0; i < 2; i++) {
      const refined =
        out.distanceTo(this.ctx.ship.object3D.position) / PROJECTILE_SPEED;
      tti = refined;
      out.copy(playerPos).addScaledVector(playerVel, tti);
    }
  }

  /** Project the (right, up) lateral aim offset into world space relative
   *  to the current line of sight. The offset always points sideways from
   *  the LOS, never into it, so the trajectory misses by at least the
   *  offset magnitude regardless of how the target is moving. */
  private computeLateralAim(
    target: THREE.Vector3,
    out: THREE.Vector3
  ): void {
    out.copy(target).sub(this.ctx.ship.object3D.position);
    const len = out.length();
    if (len < 1e-3) {
      out.copy(target);
      return;
    }
    out.multiplyScalar(1 / len);

    _perpA.copy(out).cross(WORLD_UP);
    if (_perpA.lengthSq() < 1e-4) {
      _perpA.set(1, 0, 0);
    } else {
      _perpA.normalize();
    }
    _perpB.copy(out).cross(_perpA).normalize();

    out
      .copy(target)
      .addScaledVector(_perpA, this.aimOffsetRight)
      .addScaledVector(_perpB, this.aimOffsetUp);
  }

  private updateReturning(dt: number): void {
    const distance = steerToward(
      this.ctx.ship,
      this.ctx.stationPos,
      dt,
      PATROL_SPEED,
      ACCEL,
      PATROL_TURN
    );
    if (distance < STATION_DESPAWN_RANGE) {
      this.state = 'finished';
      this.finished = true;
    }
  }

  private fireWithLead(
    playerPos: THREE.Vector3,
    playerVel: THREE.Vector3
  ): void {
    this.ctx.ship.object3D.updateMatrixWorld();
    _muzzleWorld
      .copy(MUZZLE_OFFSET)
      .applyMatrix4(this.ctx.ship.object3D.matrixWorld);

    let tti = _muzzleWorld.distanceTo(playerPos) / PROJECTILE_SPEED;
    for (let i = 0; i < 2; i++) {
      _shotDir.copy(playerPos).addScaledVector(playerVel, tti).sub(_muzzleWorld);
      tti = _shotDir.length() / PROJECTILE_SPEED;
    }
    _shotDir.normalize();
    this.ctx.hostileProjectiles.fire(
      _muzzleWorld,
      _shotDir,
      PROJECTILE_SPEED,
      GUARD_DAMAGE
    );
  }

  private randomizeAimOffset(): void {
    const theta = Math.random() * Math.PI * 2;
    this.aimOffsetRight = Math.cos(theta) * AIM_OFFSET_RADIUS;
    this.aimOffsetUp =
      Math.sin(theta) * AIM_OFFSET_RADIUS * AIM_OFFSET_VERTICAL_BIAS;
  }

  private pickPatrolWaypoint(): void {
    if (this.patrolBeat.kind === 'anchor') {
      this.patrolBeat.hub.getWorldPosition(_hubPos);
    } else {
      const endpoint =
        this.laneToggle === 0
          ? this.patrolBeat.from
          : this.patrolBeat.to;
      endpoint.getWorldPosition(_hubPos);
      this.laneToggle = (this.laneToggle === 0 ? 1 : 0) as 0 | 1;
    }
    pickRandomPoint(_hubPos, 320, 140, this.waypoint);
  }

  /** Returns the detection range used by the director to decide who to alert. */
  static readonly DETECTION_RANGE = DETECTION_RANGE;
}
